import styled from '@emotion/styled'
import {keyframes} from '@emotion/react'
import {theme} from '../../theme'

// ─── Page header ──────────────────────────────────────────────────────────────

export const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 12px ${theme.space[4]};
  // width 100%;
`
// Matches StepCardWrapper's own isAccountInfoComponent-conditional width (see
// AccountSetup.styles.js) so this header lines up with the card below it
// instead of shrinking to its own content width.
export const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: ${theme.space[4]};
  padding: 0 6px;
  width: ${({isAccountInfoComponent}) =>
    isAccountInfoComponent ? '90%' : '100%'};
`

export const PageTitle = styled.h2`
  font-size: ${theme.fontSizes.base};
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.text.primary};
  margin: 0 0 ${theme.space[1]};
`

export const PageSubtitle = styled.p`
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.text.primary};
  margin: 0;
`

export const MultiLocationBadge = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.space[2]};
  padding: ${theme.space[1]} ${theme.space[3]};
  border-radius: ${theme.radii.full};
  background: #fff3ee;
  color: #c05621;
  font-size: ${theme.fontSizes.md};
  font-weight: ${theme.fontWeights.medium};
  white-space: nowrap;
`

// ─── Draft banner ─────────────────────────────────────────────────────────────

export const DraftBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${theme.space[3]};
  padding: ${theme.space[4]} 0 0;
  border-top: 1px solid ${theme.colors.border.light};
  margin-top: ${theme.space[4]};
`

export const DraftActions = styled.div`
  display: flex;
  gap: ${theme.space[2]};
`

export const DiscardButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
  padding: ${theme.space[1]} ${theme.space[2]};
  border-radius: ${theme.radii.md};
  transition: color ${theme.transitions.normal};

  &:hover {
    color: ${theme.colors.text.primary};
  }
`

export const SaveButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.space[1]};
  font-family: ${theme.fonts.body};
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.action.secondaryText};
  background-color: ${theme.colors.action.secondary};
  border: 1px solid ${theme.colors.border.dark};
  border-radius: ${theme.radii.md};
  padding: ${theme.space[1]} ${theme.space[3]};
  cursor: pointer;
  white-space: nowrap;
  transition: background-color ${theme.transitions.fast};

  &:hover:not(:disabled) {
    background-color: ${theme.colors.action.secondaryHover};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

// ─── Accordion ────────────────────────────────────────────────────────────────

export const AccordionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.space[4]};
  width: 100%;
`
export const AccordionParentWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.space[2]};
`
export const AccordionChildWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.space[2]};
`

export const AccordionCard = styled.div`
  background: ${theme.colors.bg.primary};
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  // box-shadow: ${theme.shadows.sm};
  overflow: visible;
`

export const AccordionHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.space[4]} ${theme.space[5]};
  background: none;
  border-bottom: ${({isOpen}) =>
    isOpen ? `1px solid ${theme.colors.border.light}` : 'none'};
  cursor: pointer;
  text-align: left;
  border-radius: ${({isOpen}) =>
    isOpen ? `${theme.radii.xl} ${theme.radii.xl} 0 0` : theme.radii.xl};

  &:focus-visible {
    outline: 2px solid ${theme.colors.border.focus};
    outline-offset: -2px;
  }
`

export const AccordionHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.space[3]};
  flex: 1;
  min-width: 0;
`

export const AccordionHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.space[2]};
`

export const AccordionHActionWrap = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 6px;
`

export const ParentBadge = styled.span`
  display: flex;
  align-items: center;
  gap: ${theme.space[1]};
  padding: 4px ${theme.space[3]};
  border-radius: ${theme.radii.full};
  background: #fff3ee;
  color: #c05621;
  font-size: ${theme.fontSizes.md};
  font-weight: ${theme.fontWeights.medium};
  white-space: nowrap;
`

export const ChildBadge = styled.span`
  display: flex;
  align-items: center;
  gap: ${theme.space[1]};
  padding: 4px ${theme.space[3]};
  border-radius: ${theme.radii.full};
  background: #eff6ff;
  color: #1d4ed8;
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.medium};
  white-space: nowrap;
`

export const ChevronIcon = styled.span`
  display: flex;
  color: ${theme.colors.text.muted};
  transition: transform ${theme.transitions.normal};
  transform: ${({isOpen}) => (isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
`

export const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  border-radius: ${theme.radii.md};
  cursor: pointer;
  color: ${theme.colors.text.muted};
  transition: color ${theme.transitions.normal},
    background ${theme.transitions.normal};

  &:hover {
    color: ${theme.colors.status.danger.text};
    background: ${theme.colors.status.danger.bg};
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.border.focus};
  }
`

export const AccordionBody = styled.div`
  padding: ${theme.space[6]};
`

// ─── Form layout ──────────────────────────────────────────────────────────────

export const SectionLabel = styled.p`
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text.secondary};
  margin: ${theme.space[5]} 0 ${theme.space[2]};
  text-transform: uppercase;
  letter-spacing: 0.06em;
`

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.space[5]};
  /* Dense packing so column-pinned fields (LeftColumn/RightColumn) always backfill
     the same cells — e.g. Business Phone / Bill To stay in the right column beside
     the sales question whether or not the dependent input is showing. */
  grid-auto-flow: row dense;
`

export const FullWidth = styled.div`
  grid-column: 1 / -1;
`

// Pin a field to a specific column of the 2-col FormGrid so paired sections
// (e.g. sales attribution on the left, Bill To / Business Phone on the right)
// stay column-aligned regardless of which conditional fields are visible.
export const LeftColumn = styled.div`
  grid-column: 1;
`

export const RightColumn = styled.div`
  grid-column: 2;
`

export const HelperNote = styled.p`
  display: flex;
  align-items: flex-start;
  gap: ${theme.space[1]};
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.muted};
  margin: ${theme.space[1]} 0 0;
`

// ─── Toggle button group (Bill to, Sales rep, SST) ───────────────────────────

export const ToggleGroup = styled.div`
  display: inline-flex;
  border-radius: ${theme.radii.lg};
  background: ${theme.colors.bg.secondary};
  border: 1px solid ${theme.colors.border.default};
  padding: 3px;
  gap: 2px;
`

export const ToggleButton = styled.button`
  padding: ${theme.space[1]} ${theme.space[4]};
  font-size: ${theme.fontSizes.base};
  font-weight: ${({isActive}) =>
    isActive ? theme.fontWeights.medium : theme.fontWeights.regular};
  border: none;
  border-radius: ${theme.radii.md};
  cursor: pointer;
  transition: background ${theme.transitions.normal},
    color ${theme.transitions.normal}, box-shadow ${theme.transitions.normal};

  background: ${({isActive}) =>
    isActive ? theme.colors.bg.primary : 'transparent'};
  color: ${({isActive}) => (isActive ? theme.colors.text.primary : '#6A7181')};

  /* Active: subtle elevation to lift off the group background */
  box-shadow: ${({isActive}) =>
    isActive
      ? '0 1px 3px rgba(0,0,0,0.10), 0 1px 2px rgba(0,0,0,0.06)'
      : 'none'};

  &:hover:not(:disabled) {
    color: ${theme.colors.text.primary};
    background: ${({isActive}) =>
      isActive ? theme.colors.bg.primary : theme.colors.bg.tertiary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.border.focus};
    outline-offset: 2px;
  }
`

export const QuestionRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${theme.space[2]};
`

export const QuestionLabel = styled.p`
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.medium};
  color: #5f6b7a;
  margin: 0;
`

export const TwoColumnQuestions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.space[5]};
  margin-top: ${theme.space[6]};
`

// ─── Add child button ─────────────────────────────────────────────────────────

export const AddChildButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.space[2]};
  background: none;
  border: none;
  color: #1d4ed8;
  cursor: pointer;
  font-size: ${theme.fontSizes.base};
  font-weight: ${theme.fontWeights.semibold};
  padding: ${theme.space[2]} 0;
  margin-top: ${theme.space[1]};
  transition: opacity ${theme.transitions.fast};

  &:hover {
    opacity: 0.75;
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.border.focus};
    outline-offset: 2px;
    border-radius: ${theme.radii.sm};
  }
`
export const TopControlsRow = styled.div`
  display: grid;
  grid-template-columns: minmax(260px, 1fr) auto;
  align-items: end;
  gap: ${theme.space[5]};
  width: 100%;
  // margin: 0 0 ${theme.space[6]} 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    align-items: stretch;
  }
`

export const TransactionTypeColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;

  min-width: 0;
`

export const CountrySelectorColumn = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;

  @media (max-width: 768px) {
    justify-content: flex-start;
  }
`

// ─── Accordion header: business name (collapsed only) ─────────────────────────

export const AccordionBusinessName = styled.span`
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 320px;
`

// ─── Order button (matches MyAccounts style) ──────────────────────────────────

export const OrderButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.space[1]};
  font-family: ${theme.fonts.body};
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.action.secondaryText};
  background-color: ${theme.colors.action.secondary};
  border: 1px solid ${theme.colors.border.dark};
  border-radius: ${theme.radii.md};
  padding: ${theme.space[1]} ${theme.space[3]};
  cursor: pointer;
  white-space: nowrap;
  transition: background-color ${theme.transitions.fast};

  &:hover {
    background-color: ${theme.colors.action.secondaryHover};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${theme.colors.input.focusRing};
  }
`

// ─── Save spinner overlay (same as AccountSetup single-loc) ───────────────────

const spin = keyframes`
  to { transform: rotate(360deg); }
`

export const LoadingOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${theme.space[4]};
  z-index: ${theme.zIndex.overlay};
`

export const SpinnerRing = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: ${spin} 0.75s linear infinite;
`

export const SpinnerLabel = styled.p`
  color: #ffffff;
  font-size: ${theme.fontSizes.base};
  font-weight: ${theme.fontWeights.medium};
  margin: 0;
`
